import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import logo from "assets/brand/logo.png";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import { AuthContext } from "context/auth-context/auth.context";
import RequireAuth from "context/auth-context/require-auth.provider";
import {
  setMiniSidenav,
  useMaterialUIController,
} from "context/materialUI-context/materialUI.context";
import Sidenav from "examples/Sidenav";
import {
  JSXElementConstructor,
  Key,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import routes from "routes";

const App = () => {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, sidenavColor, darkMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { currentUserFirebase, currentAdmin } = useContext(AuthContext);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  useEffect(() => {
    if (
      currentUserFirebase?.emailVerified &&
      window.location.pathname.startsWith("/authentication")
    ) {
      navigate("/");
    }
  }, [currentAdmin, currentUserFirebase, navigate]);

  const getRoutes = (allRoutes: any[]): any =>
    allRoutes.map(
      (route: {
        collapse: any;
        route: string;
        component: ReactElement<any, string | JSXElementConstructor<any>>;
        key: Key;
        privateRoute: boolean;
      }) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          if (route.privateRoute) {
            return (
              <Route
                path={route.route}
                element={<RequireAuth>{route.component}</RequireAuth>}
                key={route.key}
              />
            );
          } else
            return (
              <Route
                path={route.route}
                element={route.component}
                key={route.key}
              />
            );
        }

        return null;
      }
    );

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />

      {layout === "dashboard" && currentUserFirebase && currentAdmin && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={logo}
            routes={routes(currentAdmin)}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
      )}

      <Routes>
        {getRoutes(routes(currentAdmin))}
        {currentUserFirebase && currentAdmin ? (
          <Route path="*" element={<Navigate to="/volontaires" />} />
        ) : (
          <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
        )}
      </Routes>
    </ThemeProvider>
  );
};

export default App;
